import { FC, useState, useMemo, useCallback } from "react";
import { useWorkspaces } from "@api/workspace.api";
import { ViewTable } from "@components/views/ViewTable";
import { useWorkspaceContext } from "@state/workspaceContext";
import { WorkspaceSortValue } from "@common/types/views/workspaceViews";
import { StickyTableFooter } from "@components/general/StickyTableFooter/StickyTableFooter";
import stickyFooterStyles from "@components/general/StickyTableFooter/StickyTableFooter.module.scss";
import { Pagination } from "@components/general/Pagination/Pagination";
import { DefaultWorkspaceView } from "./views/DefaultWorkspaceView";
import { workspaceViewConfig } from "./views/workspaceViewConfig";
import { WorkspaceDetailsRow } from "./views/cells/NameCell";
import styles from "./Workspaces.module.scss";

interface WorkspacesViewProps {
    search?: string;
}

const PAGE_SIZE = 20;

export const WorkspacesView: FC<WorkspacesViewProps> = ({ search }) => {
    const [page, setPage] = useState(1);
    const { changeActiveWorkspace } = useWorkspaceContext();
    const [currentSort, setCurrentSort] = useState<WorkspaceSortValue>(
        WorkspaceSortValue.NAME_ASC,
    );

    const changeSort = useCallback(
        (sort: WorkspaceSortValue) => {
            setCurrentSort(sort);
            setPage(1);
        },
        [setCurrentSort, setPage],
    );

    const params = useMemo(
        () => ({
            limit: PAGE_SIZE,
            page: page - 1, // Backend expects zero-based page
            filters: { search },
            sort: currentSort,
            excludeUserOrganizations: true,
        }),
        [page, currentSort, search],
    );

    const { data: workspaceResponse } = useWorkspaces(params);
    const totalPages = workspaceResponse?.pageCount ?? 1;

    const rows: WorkspaceDetailsRow[] | null = useMemo(() => {
        if (!workspaceResponse) {
            return null;
        }
        return workspaceResponse.data.map((w) => ({
            ...w,
            id: w.workspace.id,
        }));
    }, [workspaceResponse]);

    const handleRowClick = useCallback(
        (row: WorkspaceDetailsRow) => {
            changeActiveWorkspace(row.workspace);
        },
        [changeActiveWorkspace],
    );

    return (
        <>
            <ViewTable
                data={rows}
                view={DefaultWorkspaceView}
                config={workspaceViewConfig}
                sort={currentSort}
                onSortChange={changeSort}
                onRowClick={handleRowClick}
                className={styles.workspacesView}
            />
            <StickyTableFooter>
                <div className={stickyFooterStyles.row}>
                    <div className={stickyFooterStyles.rowContent}>
                        <span>{workspaceResponse?.total} workspaces</span>

                        <Pagination
                            pageCount={totalPages}
                            currentPage={page}
                            onPageChange={setPage}
                        />
                    </div>
                </div>
            </StickyTableFooter>
        </>
    );
};
